import { catalog } from '../core/catalog.js';
import { t } from '../core/i18n.js';
import { escapeHtml } from '../core/utils.js';
import { notify } from '../core/notify.js';
import { itemEdits, cleanValues, AGES } from '../core/item-edits.js';
import { captureFrame, imageFromFile, readableOrigin } from '../core/video-frame.js';
import { posterFill, frameSource } from '../core/poster-fill.js';

/* «Editar ficha» de un vídeo propio: título, año, valoración, clasificación por edades, género, sinopsis y la carátula (otro
   fotograma del vídeo). Se guarda en el dispositivo; «Restaurar» devuelve los datos de siempre. */

const field = (label, control, help = '') => `<div class="field"><label>${escapeHtml(label)}</label>${control}${help ? `<small class="field-help">${escapeHtml(help)}</small>` : ''}</div>`;

/** Nombre de fichero → algo que se pueda buscar: sin «ES|», sin (año) ni [etiquetas], sin guiones bajos ni puntos de separación. */
const searchName = (name) => String(name || '').replace(/\[[^\]]*\]|\([^)]*\)/g, ' ').replace(/^\s*[A-Z]{2,3}\s*[|:-]\s*/, '').replace(/[._]+/g, ' ').replace(/\s+/g, ' ').trim();

export function openEditItem({ kind, item, onDone = () => {} }) {
    const app = window.app;
    const id = 'edit-' + Date.now().toString(36);
    const edited = itemEdits.get(kind, item.id);
    let poster = ''; // carátula nueva elegida en esta ventana (aún sin guardar)
    let linkId = String(item.tmdb || ''); // id de TMDB al que está enlazado este título (a mano)
    let linkTitle = '';
    const url = frameSource(kind, Object.assign({}, item, { poster: '' }));
    const ageOptions = [''].concat(AGES).map((a) => `<option value="${a}" ${String(item.age || '') === a ? 'selected' : ''}>${escapeHtml(a || t('edit.ageNone'))}</option>`).join('');
    const popup = app.popup.create({
        content: `<div class="popup catpick-popup admin-popup edit-popup" id="${id}">
            <div class="page">
                <div class="navbar"><div class="navbar-bg"></div><div class="navbar-inner">
                    <div class="title">${escapeHtml(t('edit.title'))}</div>
                    <div class="right"><a href="#" class="link popup-close btn-icon" aria-label="${escapeHtml(t('common.close'))}"><span class="mi">close</span></a></div>
                </div></div>
                <div class="page-content"><form class="edit-form" data-edit novalidate>
                    <div class="edit-poster"><div class="edit-poster-img" data-edit-img>${item.poster ? `<img src="${escapeHtml(item.poster)}" alt=""/>` : '<span class="mi">movie</span>'}</div>
                        <div class="edit-poster-btns">
                            ${url ? `<button type="button" class="btn" data-edit-frame><span class="mi">image_search</span>${escapeHtml(t('edit.otherFrame'))}</button>` : ''}
                            ${catalog.searchSource ? `<button type="button" class="btn" data-edit-tmdb><span class="mi">travel_explore</span>${escapeHtml(t('edit.tmdbLink'))}</button>` : ''}
                            <button type="button" class="btn" data-edit-upload><span class="mi">add_photo_alternate</span>${escapeHtml(t('edit.upload'))}</button>
                            <input type="file" accept="image/*" hidden data-edit-file/>
                        </div></div>
                    ${catalog.searchSource ? `<div class="edit-tmdb-linked" data-tmdb-linked ${linkId ? '' : 'hidden'}><span class="mi">link</span><span data-tmdb-linked-text></span><button type="button" class="btn" data-tmdb-unlink><span class="mi">link_off</span>${escapeHtml(t('edit.tmdbUnlink'))}</button></div>
                    <div class="edit-tmdb" data-tmdb-panel hidden>
                        <p class="pw-note">${escapeHtml(t('edit.tmdbHint'))}</p>
                        <div class="edit-row" data-nav-row>
                            ${field(t('edit.name'), `<input name="tmdbq" value="${escapeHtml(searchName(item.name))}" maxlength="100" autocomplete="off"/>`)}
                            ${field(t('edit.year'), `<input name="tmdby" value="${escapeHtml(item.year || '')}" inputmode="numeric" maxlength="4" autocomplete="off"/>`)}
                        </div>
                        <button type="button" class="btn btn-block" data-tmdb-search><span class="mi">search</span>${escapeHtml(t('match.search'))}</button>
                        <div class="field-error" data-tmdb-err hidden></div>
                        <div data-tmdb-results></div>
                    </div>` : ''}
                    ${field(t('edit.name'), `<input name="name" value="${escapeHtml(item.name || '')}" maxlength="200" autocomplete="off" data-nav-default/>`)}
                    <div class="edit-row">
                        ${field(t('edit.year'), `<input name="year" value="${escapeHtml(item.year || '')}" inputmode="numeric" maxlength="4" autocomplete="off"/>`)}
                        ${field(t('edit.rating'), `<input name="rating" value="${item.rating ? escapeHtml(String(item.rating)) : ''}" inputmode="decimal" maxlength="4" autocomplete="off"/>`, t('edit.ratingHelp'))}
                    </div>
                    ${field(t('edit.age'), `<select name="age">${ageOptions}</select>`)}
                    ${field(t('edit.genre'), `<input name="genre" value="${escapeHtml(item.genre || '')}" placeholder="${escapeHtml(t('edit.genrePh'))}" autocomplete="off"/>`)}
                    ${field(t('edit.plot'), `<textarea name="plot" rows="5">${escapeHtml(item.plot || '')}</textarea>`)}
                    <div class="field-error" data-error hidden></div>
                    <button type="submit" class="btn btn-accent btn-lg btn-block"><span class="mi">check</span>${escapeHtml(t('common.save'))}</button>
                    ${edited ? `<button type="button" class="btn btn-block" data-edit-restore><span class="mi">undo</span>${escapeHtml(t('edit.restore'))}</button>` : ''}
                </form></div>
            </div>
        </div>`,
        on: {
            open(p) {
                const el = p.$el[0];
                const form = el.querySelector('[data-edit]');
                const showError = (key) => { const e = el.querySelector('[data-error]'); e.textContent = key ? t('edit.err.' + key) : ''; e.hidden = !key; };
                const setPosterPreview = (src) => {
                    poster = src;
                    el.querySelector('[data-edit-img]').innerHTML = `<img src="${escapeHtml(src)}" alt=""/>`;
                };
                const frameBtn = el.querySelector('[data-edit-frame]');
                if (frameBtn) {
                    frameBtn.addEventListener('click', async () => {
                        frameBtn.disabled = true;
                        const prev = frameBtn.innerHTML;
                        frameBtn.innerHTML = `<div class="spinner" style="width:18px;height:18px;border-width:2px"></div>${escapeHtml(t('edit.capturing'))}`;
                        // Un punto al azar entre el 5 % y el 90 % del vídeo.
                        const shot = await captureFrame(url, { fractions: [0.05 + Math.random() * 0.85], timeoutMs: 30000 });
                        frameBtn.disabled = false;
                        frameBtn.innerHTML = prev;
                        if (!shot) { notify.error(t('edit.captureFailed') + ' ' + t('edit.captureFailedHint')); return; }
                        setPosterPreview(shot);
                    });
                }
                const fileInput = el.querySelector('[data-edit-file]');
                el.querySelector('[data-edit-upload]').addEventListener('click', () => fileInput.click());
                fileInput.addEventListener('change', async () => {
                    const file = fileInput.files && fileInput.files[0];
                    fileInput.value = '';
                    if (!file) return;
                    const img = await imageFromFile(file);
                    if (!img) { notify.error(t('edit.imageFailed')); return; }
                    setPosterPreview(img);
                });
                // Enlazar a mano con TMDB: busca, se elige y se rellenan carátula, año, valoración, género, sinopsis y edad (se pueden cambiar antes de guardar).
                const panel = el.querySelector('[data-tmdb-panel]');
                if (panel) {
                    const box = panel.querySelector('[data-tmdb-results]');
                    const err = panel.querySelector('[data-tmdb-err]');
                    const linkedBox = el.querySelector('[data-tmdb-linked]');
                    const linkedText = el.querySelector('[data-tmdb-linked-text]');
                    let found = [];
                    const showLinked = () => {
                        linkedBox.hidden = !linkId;
                        linkedText.textContent = linkId ? t('edit.tmdbLinked', { title: linkTitle || '#' + linkId }) : '';
                    };
                    showLinked();
                    const mapAge = (c) => {
                        const s = String(c || '').trim().toUpperCase();
                        if (!s) return '';
                        if (/^(TP|G|AA|APTA|ALL|0)$/.test(s)) return 'TP';
                        const n = parseInt(s.replace(/[^0-9]/g, ''), 10);
                        if (!isFinite(n)) return /^(PG|PG-13)$/.test(s) ? (s === 'PG' ? '+7' : '+12') : s === 'R' ? '+16' : s === 'NC-17' ? '+18' : '';
                        return n >= 18 ? '+18' : n >= 16 ? '+16' : n >= 12 ? '+12' : n >= 7 ? '+7' : 'TP';
                    };
                    const list = () => found.map((c) => `<button type="button" class="admin-user" data-tmdb-pick="${escapeHtml(String(c.tmdbId))}" style="width:100%;text-align:left">
                            <span class="rmt-thumb" style="width:44px;aspect-ratio:2/3;flex:none;background:var(--surface-2) center/cover no-repeat${/^https:/.test(c.thumb) ? `;background-image:url('${escapeHtml(c.thumb)}')` : ''}"></span>
                            <div class="admin-user-body"><div class="admin-user-name">${escapeHtml(c.title)}${c.year ? ` <small>(${escapeHtml(c.year)})</small>` : ''}</div><div class="admin-user-sub">${escapeHtml(c.overview || c.originalTitle || '')}</div></div>
                        </button>`).join('');
                    const search = async () => {
                        err.hidden = true;
                        box.innerHTML = '<div class="spinner"></div>';
                        try {
                            const r = await catalog.searchSource(kind, { query: form.elements.tmdbq.value.trim(), year: form.elements.tmdby.value.trim() });
                            found = (r && r.results) || [];
                            box.innerHTML = list() || `<p class="pw-note">${escapeHtml(t('edit.tmdbNone'))}</p>`;
                        } catch (er) {
                            box.innerHTML = '';
                            err.textContent = t('edit.tmdbError');
                            err.hidden = false;
                        }
                    };
                    el.querySelector('[data-edit-tmdb]').addEventListener('click', () => {
                        panel.hidden = !panel.hidden;
                        if (!panel.hidden && !found.length) search();
                        if (!panel.hidden) form.elements.tmdbq.focus();
                    });
                    panel.querySelector('[data-tmdb-search]').addEventListener('click', search);
                    ['tmdbq', 'tmdby'].forEach((n) => form.elements[n].addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); search(); } }));
                    el.querySelector('[data-tmdb-unlink]').addEventListener('click', () => { linkId = ''; linkTitle = ''; showLinked(); });
                    box.addEventListener('click', async (e) => {
                        const b = e.target.closest('[data-tmdb-pick]');
                        if (!b) return;
                        const c = found.find((x) => String(x.tmdbId) === b.getAttribute('data-tmdb-pick'));
                        if (!c) return;
                        b.disabled = true;
                        let full = null;
                        try { full = await catalog.enricher(kind, { tmdb: c.tmdbId }); } catch (er) { full = null; }
                        if (!full || !full.tmdbId) full = { year: c.year, overview: c.overview, poster: c.poster, genres: '', rating: 0, certification: '' };
                        const set = (n, v) => { if (v !== undefined && v !== null && v !== '') form.elements[n].value = v; };
                        set('year', full.year || c.year);
                        set('rating', full.rating ? String(full.rating) : '');
                        set('genre', full.genres);
                        set('plot', full.overview || c.overview);
                        const age = mapAge(full.certification);
                        if (age && AGES.indexOf(age) !== -1) form.elements.age.value = age;
                        const art = full.poster || c.poster;
                        if (/^https:/.test(art || '')) setPosterPreview(art);
                        linkId = String(c.tmdbId);
                        linkTitle = c.title + (c.year ? ' (' + c.year + ')' : '');
                        showLinked();
                        panel.hidden = true;
                        notify.toast(t('edit.tmdbPicked'), { icon: 'link', duration: 2200 });
                    });
                }
                form.addEventListener('submit', async (e) => {
                    e.preventDefault();
                    const data = {};
                    ['name', 'year', 'rating', 'age', 'genre', 'plot'].forEach((n) => { data[n] = form.elements[n].value; });
                    data.tmdb = linkId;
                    const r = cleanValues(data);
                    if (r.error) { showError(r.error); return; }
                    showError('');
                    await itemEdits.save(kind, item.id, r.values);
                    if (poster) await posterFill.setPoster(kind, item.id, poster);
                    notify.toast(t('edit.saved'), { icon: 'check_circle', duration: 1800 });
                    popup.close();
                    onDone();
                });
                const restore = el.querySelector('[data-edit-restore]');
                if (restore) {
                    restore.addEventListener('click', async () => {
                        await itemEdits.restore(kind, item.id);
                        notify.toast(t('edit.restored'), { icon: 'undo', duration: 1800 });
                        popup.close();
                        onDone();
                    });
                }
            },
            opened(p) { if (window.IPTV && window.IPTV.device && window.IPTV.device.isTV) setTimeout(() => window.IPTV.tvnav.focusLayer(p.$el[0]), 80); },
            closed() {
                try { popup.destroy(); } catch (e) { /* ignore */ }
                const el = document.getElementById(id);
                if (el && el.parentNode) el.parentNode.removeChild(el);
            }
        }
    });
    popup.open();
    return popup;
}

export { readableOrigin };
